import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ShoppingCart, ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAccount } from '../../context/AccountContext';
import {
  GET_EBAY_SETTINGS,
  GET_EBAY_POLICIES,
  BEGIN_EBAY_AUTH,
  UPDATE_EBAY_SETTINGS,
  DISCONNECT_EBAY,
  REFRESH_EBAY_POLICIES,
} from '../../graphql';
import { Loader } from '../common/PageLoader';

/**
 * eBay connection.
 *
 * Nothing on this screen is a credential. Sellers grant access through eBay's
 * own consent flow, and the refresh token that results is held server-side in
 * the secret store — it never reaches the browser, and the API server itself
 * never reads it back. What is editable here is listing preference:
 * marketplace, category, policies, how titles are built.
 *
 * Business policies are a snapshot: the server's background job fetches them
 * from eBay (right after connecting, daily, and on Refresh) and this screen
 * shows the lists with the time they were taken. Refresh asks for a new
 * snapshot and polls until it lands.
 */

// How often to ask for the snapshot after a refresh, and for how long.
const POLL_EVERY_MS = 3000;
const POLL_FOR_MS = 45_000;

const inputClassName =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

const MARKETPLACES = ['EBAY_US', 'EBAY_GB', 'EBAY_CA', 'EBAY_AU', 'EBAY_DE'];

const CONDITIONS = [
  { value: 'NEW', label: 'New' },
  { value: 'LIKE_NEW', label: 'Like new' },
  { value: 'USED_VERY_GOOD', label: 'Used — very good' },
  { value: 'USED_GOOD', label: 'Used — good' },
  { value: 'USED_ACCEPTABLE', label: 'Used — acceptable' },
];

const TOKENS = [
  '{year}',
  '{manufacturer}',
  '{set}',
  '{number}',
  '{player}',
  '{variant}',
  '{grade}',
];

export function EbayConnectionSettings() {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const [form, setForm] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');

  const { data, loading, refetch } = useQuery(GET_EBAY_SETTINGS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const [beginAuth, { loading: starting }] = useMutation(BEGIN_EBAY_AUTH);
  const [updateSettings, { loading: saving }] = useMutation(UPDATE_EBAY_SETTINGS);
  const [disconnect] = useMutation(DISCONNECT_EBAY);
  const [refreshPolicies] = useMutation(REFRESH_EBAY_POLICIES);

  const s = data?.ebaySettings;

  const {
    data: policyData,
    startPolling,
    stopPolling,
  } = useQuery(GET_EBAY_POLICIES, {
    variables: { accountId },
    skip: !accountId || !s?.connected,
    fetchPolicy: 'cache-and-network',
  });
  const policies = policyData?.ebayPolicies;

  // A refresh is "in flight" from the moment we ask until syncedAt moves (or an
  // error lands), bounded by POLL_FOR_MS so a stalled job cannot spin forever.
  const [refreshing, setRefreshing] = useState<{ since: number; baseline: string | null } | null>(
    null
  );

  useEffect(() => {
    if (!refreshing) return;
    startPolling(POLL_EVERY_MS);
    const timer = setTimeout(() => setRefreshing(null), POLL_FOR_MS);
    return () => {
      clearTimeout(timer);
      stopPolling();
    };
  }, [refreshing, startPolling, stopPolling]);

  useEffect(() => {
    if (!refreshing || !policies) return;
    const landed = (policies.syncedAt || null) !== refreshing.baseline || Boolean(policies.error);
    if (landed) setRefreshing(null);
  }, [policies, refreshing]);

  const askForPolicies = () => {
    setRefreshing({ since: Date.now(), baseline: policies?.syncedAt || null });
  };

  useEffect(() => {
    if (!s) return;
    setForm({
      marketplaceId: s.marketplaceId || 'EBAY_US',
      defaultCategoryId: s.defaultCategoryId || '',
      paymentPolicyId: s.paymentPolicyId || '',
      returnPolicyId: s.returnPolicyId || '',
      fulfillmentPolicyId: s.fulfillmentPolicyId || '',
      titlePattern: s.titlePattern || '',
      defaultCondition: s.defaultCondition || 'USED_VERY_GOOD',
      defaultQuantity: String(s.defaultQuantity ?? 1),
    });
  }, [s]);

  // The callback bounces back here with ?ebay=connected|denied|error.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('ebay');
    if (!result) return;
    if (result === 'connected') {
      setMessage('eBay connected.');
      // The server asked its background job for the policies as part of
      // connecting; watch for them to arrive.
      setRefreshing({ since: Date.now(), baseline: null });
    } else if (result === 'denied') setMessage('You declined the eBay authorization.');
    else setMessage(params.get('message') || 'Could not connect to eBay.');
    void refetch();
  }, [refetch]);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const handleConnect = async () => {
    if (!accountId) return;
    const { data: r } = await beginAuth({
      variables: { accountId, returnTo: window.location.pathname + window.location.search },
    });
    const url = r?.beginEbayAuth?.url;
    // Full navigation, not a popup: eBay's consent page refuses to be framed
    // and popup blockers eat the alternative.
    if (url) window.location.assign(url);
  };

  const handleDisconnect = async () => {
    if (!accountId) return;
    if (
      !window.confirm(
        'Disconnect eBay?\n\nDrafts already on eBay stay there. You will need to reconnect before pushing any more listings.'
      )
    ) {
      return;
    }
    await disconnect({ variables: { accountId } });
    setMessage('eBay disconnected.');
    await refetch();
  };

  const handleSave = async () => {
    if (!accountId) return;
    await updateSettings({
      variables: {
        accountId,
        input: {
          marketplaceId: form.marketplaceId || null,
          defaultCategoryId: form.defaultCategoryId || null,
          paymentPolicyId: form.paymentPolicyId || null,
          returnPolicyId: form.returnPolicyId || null,
          fulfillmentPolicyId: form.fulfillmentPolicyId || null,
          titlePattern: form.titlePattern || null,
          defaultCondition: form.defaultCondition || null,
          defaultQuantity: Number(form.defaultQuantity) || 1,
        },
      },
    });
    setMessage('Saved.');
    await refetch();
  };

  const handleRefreshPolicies = async () => {
    if (!accountId) return;
    askForPolicies();
    try {
      await refreshPolicies({ variables: { accountId } });
    } catch (err) {
      setRefreshing(null);
      setMessage(err instanceof Error ? err.message : 'Could not ask for a refresh.');
    }
  };

  // Connected, nothing fetched yet, nothing failed: the first snapshot is on
  // its way (the connect flow queues it).
  const policiesPending = Boolean(
    s?.connected && policies && !policies.syncedAt && !policies.error
  );

  if (accountsLoading || loading) return <Loader label="Loading" className="p-6 text-cmp-ink-2" />;

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-cmp-ink flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" /> eBay
        </h2>
        <p className="text-sm text-cmp-ink-2 mt-1">
          Connect your eBay seller account to push cards as draft listings. You authorize through
          eBay — we never see your eBay password or API keys.
        </p>
      </div>

      <Card className="p-4 space-y-3 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            {s?.connected ? (
              <>
                <Badge>connected</Badge>
                <span className="text-sm text-cmp-ink">{s.ebayUsername || 'eBay account'}</span>
              </>
            ) : (
              <Badge variant="outline">not connected</Badge>
            )}
            <Badge variant="outline" className="text-[10px]">
              {s?.environment || 'production'}
            </Badge>
          </div>
          {s?.connected ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => void handleConnect()}>
                Reconnect
              </Button>
              <Button variant="ghost" size="sm" onClick={() => void handleDisconnect()}>
                Disconnect
              </Button>
            </div>
          ) : (
            <Button onClick={() => void handleConnect()} disabled={starting} className="gap-2">
              <ExternalLink className="w-4 h-4" /> Connect eBay
            </Button>
          )}
        </div>

        {s?.environmentMismatch && (
          <p className="text-xs text-amber-400 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            This connection was made against eBay {s.environment}, but the server now uses a
            different environment. Reconnect before pushing listings.
          </p>
        )}

        {s?.expiringSoon && !s?.environmentMismatch && (
          <p className="text-xs text-amber-400 flex items-start gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            Your eBay authorization expires{' '}
            {s.refreshExpiresAt ? new Date(s.refreshExpiresAt).toLocaleDateString() : 'soon'}.
            Reconnect to avoid interruption.
          </p>
        )}

        {message && <p className="text-xs text-cmp-ink-2">{message}</p>}
      </Card>

      <Card className="p-4 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <h3 className="text-sm font-medium text-cmp-ink">Listing defaults</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="marketplace">Marketplace</Label>
            <select
              id="marketplace"
              value={form.marketplaceId || 'EBAY_US'}
              onChange={(e) => set('marketplaceId', e.target.value)}
              className={inputClassName}
            >
              {MARKETPLACES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="category">Default category ID</Label>
            <Input
              id="category"
              value={form.defaultCategoryId || ''}
              onChange={(e) => set('defaultCategoryId', e.target.value)}
              placeholder="261328 (Baseball cards)"
              className={inputClassName}
            />
          </div>
          <div>
            <Label htmlFor="condition">Default condition</Label>
            <select
              id="condition"
              value={form.defaultCondition || 'USED_VERY_GOOD'}
              onChange={(e) => set('defaultCondition', e.target.value)}
              className={inputClassName}
            >
              {CONDITIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="qty">Default quantity</Label>
            <Input
              id="qty"
              type="number"
              min={0}
              value={form.defaultQuantity || '1'}
              onChange={(e) => set('defaultQuantity', e.target.value)}
              className={inputClassName}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="title">Title pattern</Label>
          <Input
            id="title"
            value={form.titlePattern || ''}
            onChange={(e) => set('titlePattern', e.target.value)}
            className={inputClassName}
          />
          <p className="text-[11px] text-cmp-ink-2 mt-1">Tokens: {TOKENS.join('  ')}</p>
        </div>

        {/* Policies are eBay's, so they can only be listed once connected. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <Label>Business policies</Label>
              {s?.connected && policies?.syncedAt && (
                <p className="text-[11px] text-cmp-ink-2">
                  Policies as of {new Date(policies.syncedAt).toLocaleString()}
                </p>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              disabled={!s?.connected || Boolean(refreshing)}
              onClick={() => void handleRefreshPolicies()}
            >
              <RefreshCw className="w-3 h-3" />
              {refreshing ? <Loader size="inline" /> : 'Refresh'}
            </Button>
          </div>
          {!s?.connected && (
            <p className="text-[11px] text-cmp-ink-2">
              Connect eBay to load your payment, return and shipping policies. eBay requires all
              three before an offer can be published.
            </p>
          )}
          {policiesPending && (
            <p className="text-xs text-cmp-ink-2 flex items-center gap-2">
              <Loader size="inline" /> Connecting… your policies arrive in a moment.
            </p>
          )}
          {refreshing && !policiesPending && (
            <p className="text-[11px] text-cmp-ink-2">Asking eBay for your latest policies…</p>
          )}
          {policies?.error && (
            <p className="text-xs text-amber-400 flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              The last refresh did not work: {policies.error}
            </p>
          )}
          {s?.connected && policies?.syncedAt && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(
                [
                  ['paymentPolicyId', 'Payment', policies.payment],
                  ['returnPolicyId', 'Return', policies.return],
                  ['fulfillmentPolicyId', 'Shipping', policies.fulfillment],
                ] as const
              ).map(([key, label, list]) => (
                <div key={key}>
                  <Label htmlFor={key}>{label}</Label>
                  <select
                    id={key}
                    value={form[key] || ''}
                    onChange={(e) => set(key, e.target.value)}
                    className={inputClassName}
                  >
                    <option value="">— none —</option>
                    {(list || []).map((p: { id: string; name: string }) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Card>
    </div>
  );
}
